import React, { useState, useEffect, useRef } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useAuth } from 'context/AuthContext';
import {
  subscribeToNotifications,
  markNotificationRead,
  markNotificationsRead,
} from 'services/notificationsService';
import {
  MapPin,
  Plus,
  Calendar,
  User,
  LogOut,
  Bell,
  Settings,
  LayoutGrid,
  CreditCard,
  UserCog,
  Shield,
  Heart,
  Globe,
  Check,
  Ticket,
} from 'lucide-react';
import LanguageSwitcher from 'components/LanguageSwitcher';
import './styles.css';
import { ROUTES } from 'constants/routes';

// ─── Module-scope components (stable references — defined outside Navbar to
//     prevent React from unmounting/remounting the dropdown on every parent
//     render) ────────────────────────────────────────────────────────────────

const LANG_OPTIONS = [
  { code: 'en', label: 'English', flag: 'gb' },
  { code: 'pt-BR', label: 'Português (Brasil)', flag: 'br' },
];

const LanguageRow = ({ currentCode, onPick }) => {
  const isActive = (code) =>
    currentCode === code || (code === 'pt-BR' && currentCode?.startsWith('pt'));
  return (
    <>
      <div
        style={{
          padding: '8px 14px 4px',
          fontSize: 11,
          fontWeight: 700,
          letterSpacing: 0.8,
          textTransform: 'uppercase',
          color: 'var(--text-dim)',
          display: 'flex',
          alignItems: 'center',
          gap: 6,
        }}
      >
        <Globe size={12} /> Language
      </div>
      {LANG_OPTIONS.map((lang) => (
        <button
          key={lang.code}
          type="button"
          className="avatar-dd-item"
          onClick={() => onPick(lang.code)}
          style={{ width: '100%', display: 'flex', alignItems: 'center', gap: 10 }}
        >
          <img
            src={`https://flagcdn.com/w40/${lang.flag}.png`}
            alt=""
            width={20}
            height={14}
            style={{ borderRadius: 2, objectFit: 'cover' }}
          />
          <span style={{ flex: 1, textAlign: 'left' }}>{lang.label}</span>
          {isActive(lang.code) && <Check size={14} style={{ color: 'var(--accent)' }} />}
        </button>
      ))}
    </>
  );
};

const AvatarMenu = ({
  isRealUser,
  userProfile,
  user,
  isGymOwner,
  isCoach,
  isMasterAdmin,
  i18n,
  t,
  onClose,
  onSignOut,
}) => (
  <>
    {isRealUser ? (
      <>
        <div className="avatar-dd-user">
          <span className="avatar-dd-name">{userProfile?.displayName || t('common.you')}</span>
          <span className="avatar-dd-email">{user.email}</span>
          {(isGymOwner || isCoach) && (
            <span className="badge badge-accent" style={{ alignSelf: 'flex-start', marginTop: 4 }}>
              {isCoach ? t('navbar.coachBadge') : t('navbar.gymOwnerBadge')}
            </span>
          )}
        </div>
        <div className="avatar-dd-divider" />
        <Link to={ROUTES.account} className="avatar-dd-item" onClick={onClose}>
          <UserCog size={15} /> {t('navbar.accountSettings')}
        </Link>
        {isMasterAdmin && (
          <Link to={ROUTES.admin.hub} className="avatar-dd-item" onClick={onClose}>
            <Shield size={15} /> {t('navbar.adminPanel')}
          </Link>
        )}
        {isGymOwner || isCoach ? (
          <>
            <Link to={ROUTES.gymDashboard} className="avatar-dd-item" onClick={onClose}>
              <LayoutGrid size={15} /> {t('navbar.dashboard')}
            </Link>
            <Link to={ROUTES.profile} className="avatar-dd-item" onClick={onClose}>
              <Settings size={15} /> {isCoach ? t('navbar.coachProfile') : t('navbar.gymProfile')}
            </Link>
          </>
        ) : (
          <>
            <Link to={ROUTES.savedEvents} className="avatar-dd-item" onClick={onClose}>
              <Heart size={15} /> {t('navbar.savedEvents')}
            </Link>
            <Link to={ROUTES.myBookings} className="avatar-dd-item" onClick={onClose}>
              <CreditCard size={15} /> {t('navbar.myBookingsAndPayments')}
            </Link>
          </>
        )}
        <div className="avatar-dd-divider" />
        <LanguageRow
          currentCode={i18n.resolvedLanguage}
          onPick={(code) => i18n.changeLanguage(code)}
        />
        <div className="avatar-dd-divider" />
        <button className="avatar-dd-item avatar-dd-signout" onClick={onSignOut}>
          <LogOut size={15} /> {t('common.signOut')}
        </button>
      </>
    ) : (
      <>
        <Link to={ROUTES.login} className="avatar-dd-item" onClick={onClose}>
          {t('common.logIn')}
        </Link>
        <Link to={ROUTES.signup} className="avatar-dd-item" onClick={onClose}>
          {t('common.signUpFree')}
        </Link>
        <div className="avatar-dd-divider" />
        <LanguageRow
          currentCode={i18n.resolvedLanguage}
          onPick={(code) => i18n.changeLanguage(code)}
        />
      </>
    )}
  </>
);

// Notifications are stored as `type` + `params` rather than rendered copy, so
// the same document reads correctly in whichever language the viewer picks.
const notifText = (t, n, part) =>
  t([`notifications.items.${n.type}.${part}`, 'notifications.items.fallback'], n.params || {});

// NotifBell renders both its trigger button and its own popover panel.
// Each instance gets a unique popover ID (via the `id` prop) and a CSS class
// (`notif-bell--desktop` / `notif-bell--mobile`) so anchor-name and
// position-anchor are fully defined in CSS — no inline styles needed.
const NotifBell = ({
  id,
  notifications,
  unreadCount,
  onMarkRead,
  onMarkAllRead,
  onNavigate,
  t,
}) => {
  const popoverId = `notif-panel-${id}`;
  const panelRef = useRef(null);

  return (
    <div className="notif-wrapper">
      <button
        className={`btn btn-ghost btn-sm notif-bell notif-bell--${id}`}
        popoverTarget={popoverId}
        aria-label={t('notifications.title')}
      >
        <Bell size={18} />
        {unreadCount > 0 && <span className="notif-badge">{unreadCount}</span>}
      </button>

      <div
        id={popoverId}
        popover="auto"
        ref={panelRef}
        className={`notif-dropdown notif-dropdown--${id}`}
      >
        <div className="notif-header">
          <strong>{t('notifications.title')}</strong>
          {unreadCount > 0 && (
            <button className="notif-mark-read" onClick={onMarkAllRead}>
              {t('notifications.markAllRead')}
            </button>
          )}
        </div>
        {notifications.length === 0 ? (
          <div className="notif-empty">{t('notifications.empty')}</div>
        ) : (
          <div className="notif-list">
            {notifications.map((n) => (
              <div
                key={n.id}
                className={`notif-item ${!n.read ? 'unread' : ''}`}
                onClick={() => {
                  onMarkRead(n.id);
                  if (n.eventId) onNavigate(`/event/${n.eventId}`);
                  panelRef.current?.hidePopover();
                }}
              >
                <div className="notif-dot">{!n.read && <span />}</div>
                <div className="notif-content">
                  <span className="notif-title">{notifText(t, n, 'title')}</span>
                  <span className="notif-msg">{notifText(t, n, 'message')}</span>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
};

// ─── Navbar ────────────────────────────────────────────────────────────────

const Navbar = () => {
  const { t, i18n } = useTranslation();
  const { user, userProfile, signOut, isGymOwner, canPublishEvents, isAdmin, isCoach } = useAuth();
  const isRealUser = user && !user.isAnonymous;
  const isMasterAdmin = isAdmin;

  const location = useLocation();
  const isHome = location.pathname === '/';
  const navigate = useNavigate();

  // Refs for programmatic close only (e.g. after navigation or sign-out).
  // Click-outside and Escape dismissal are handled natively by popover="auto".
  const desktopMenuRef = useRef(null);
  const mobileTopbarRef = useRef(null);
  const mobileBnavRef = useRef(null);

  const closeMenu = () => {
    [desktopMenuRef, mobileTopbarRef, mobileBnavRef].forEach((ref) => {
      if (ref.current?.matches(':popover-open')) ref.current.hidePopover();
    });
  };

  const handleSignOut = async () => {
    closeMenu();
    await signOut();
    navigate(ROUTES.home);
  };

  const isActive = (path) => location.pathname === path;

  // ── Notifications ────────────────────────────────────────────────────────
  const [notifications, setNotifications] = useState([]);

  useEffect(() => {
    if (!isRealUser) return;
    const unsub = subscribeToNotifications(user.uid, setNotifications);
    return () => unsub();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user]);

  const unreadCount = notifications.filter((n) => !n.read).length;

  const markAsRead = (id) => markNotificationRead(id);

  const markAllRead = () => markNotificationsRead(notifications);

  const avatarMenuProps = {
    isRealUser,
    userProfile,
    user,
    isGymOwner,
    isCoach,
    isMasterAdmin,
    i18n,
    t,
    onClose: closeMenu,
    onSignOut: handleSignOut,
  };

  const notifBellProps = {
    notifications,
    unreadCount,
    onMarkRead: markAsRead,
    onMarkAllRead: markAllRead,
    onNavigate: navigate,
    t,
  };

  return (
    <>
      {/* ── Top bar ── */}
      <div className={`navbar-container${isHome ? ' navbar-home' : ''}`}>
        <nav className="navbar">
          <div className="navbar-inner">
            {/* Logo */}
            <Link to={ROUTES.home} className="navbar-logo">
              <img src="/logo-horizontal.png" alt="OpenMat" className="navbar-logo-img" />
            </Link>

            {/* Desktop nav links */}
            <div className="navbar-links desktop-only">
              <Link
                to={ROUTES.discover}
                className={`nav-link ${isActive('/discover') ? 'active' : ''}`}
              >
                <MapPin size={16} /> {t('navbar.map')}
              </Link>
              {/* Open Mat's own camps and gatherings — distinct from the events
                  gyms list on the map, hence "Our Events" rather than "Events". */}
              <Link
                to={ROUTES.openMatEvents}
                className={`nav-link ${isActive('/openmat-events') ? 'active' : ''}`}
              >
                <Ticket size={16} /> {t('navbar.ourEvents')}
              </Link>
              {isRealUser && !canPublishEvents && (
                <Link
                  to={ROUTES.myBookings}
                  className={`nav-link ${isActive('/my-bookings') ? 'active' : ''}`}
                >
                  <Calendar size={16} /> {t('navbar.myBookings')}
                </Link>
              )}
              {canPublishEvents && (
                <Link
                  to={ROUTES.createEvent}
                  className={`nav-link ${isActive('/create-event') ? 'active' : ''}`}
                >
                  <Plus size={16} /> {t('navbar.postEvent')}
                </Link>
              )}
              {(isGymOwner || isCoach) && (
                <Link
                  to={ROUTES.gymDashboard}
                  className={`nav-link ${isActive('/gym-dashboard') ? 'active' : ''}`}
                >
                  {t('navbar.dashboard')}
                </Link>
              )}
              {(isGymOwner || isCoach) && (
                <Link
                  to={ROUTES.profile}
                  className={`nav-link ${isActive('/profile') ? 'active' : ''}`}
                >
                  <Settings size={16} />{' '}
                  {isCoach ? t('navbar.coachProfile') : t('navbar.gymProfile')}
                </Link>
              )}
              {isMasterAdmin && (
                <Link
                  to={ROUTES.admin.hub}
                  className={`nav-link ${isActive('/admin') ? 'active' : ''}`}
                >
                  <Shield size={16} /> {t('navbar.admin')}
                </Link>
              )}
            </div>

            {/* Desktop right */}
            <div className="navbar-right desktop-only">
              <LanguageSwitcher />
              {isRealUser && <NotifBell id="desktop" {...notifBellProps} />}
              {isRealUser ? (
                <button
                  className="user-menu avatar-trigger--desktop"
                  popoverTarget="desktop-avatar-menu"
                  aria-label={t('navbar.openProfileMenu')}
                >
                  <div className="user-avatar">
                    {userProfile?.photoURL ? (
                      <img src={userProfile.photoURL} alt="" />
                    ) : (
                      <User size={18} />
                    )}
                  </div>
                  <span className="user-name">{userProfile?.displayName || user.email}</span>
                </button>
              ) : (
                <div className="auth-buttons">
                  <Link to={ROUTES.login} className="btn btn-ghost btn-sm">
                    {t('common.logInShort')}
                  </Link>
                  <Link to={ROUTES.signup} className="btn btn-primary btn-sm">
                    {t('common.signUp')}
                  </Link>
                </div>
              )}
            </div>

            {/* Mobile top-right: language + avatar. No bell here — on phones
                this container is display:none on every route except '/', and
                Landing bounces logged-in users off '/', so a bell mounted here
                would never be reachable. It lives in .mobile-notif-overlay
                below instead. */}
            <div className="mobile-topbar-right mobile-only">
              <LanguageSwitcher compact />
              <button
                className="mobile-avatar-btn avatar-trigger--mobile-topbar"
                popoverTarget="mobile-topbar-menu"
                aria-label={t('navbar.openProfileMenu')}
              >
                {userProfile?.photoURL ? (
                  <img src={userProfile.photoURL} alt="" className="mobile-avatar-img" />
                ) : (
                  <User size={18} />
                )}
              </button>
            </div>
          </div>
        </nav>
      </div>

      {/* ── Mobile bottom nav ── */}
      <nav className="bottom-nav mobile-only">
        <Link to={ROUTES.discover} className={`bnav-item ${isActive('/discover') ? 'active' : ''}`}>
          <MapPin size={22} />
          <span>{t('navbar.map')}</span>
        </Link>

        <Link
          to={ROUTES.openMatEvents}
          className={`bnav-item ${isActive('/openmat-events') ? 'active' : ''}`}
        >
          <Ticket size={22} />
          <span>{t('navbar.ourEventsShort')}</span>
        </Link>

        {isRealUser && !canPublishEvents && (
          <Link
            to={ROUTES.savedEvents}
            className={`bnav-item ${isActive('/saved-events') ? 'active' : ''}`}
          >
            <Heart size={22} />
            <span>{t('navbar.saved')}</span>
          </Link>
        )}

        {isRealUser && !canPublishEvents && (
          <Link
            to={ROUTES.myBookings}
            className={`bnav-item ${isActive('/my-bookings') ? 'active' : ''}`}
          >
            <Calendar size={22} />
            <span>{t('navbar.myBookingsShort')}</span>
          </Link>
        )}

        {isRealUser && (isGymOwner || isCoach) && (
          <Link
            to={ROUTES.gymDashboard}
            className={`bnav-item ${isActive('/gym-dashboard') ? 'active' : ''}`}
          >
            <LayoutGrid size={22} />
            <span>{t('navbar.dashboard')}</span>
          </Link>
        )}

        {isRealUser && canPublishEvents && (
          <Link
            to={ROUTES.createEvent}
            className={`bnav-item ${isActive('/create-event') ? 'active' : ''}`}
          >
            <Plus size={22} />
            <span>{t('navbar.postShort')}</span>
          </Link>
        )}

        {isRealUser && isMasterAdmin && (
          <Link to={ROUTES.admin.hub} className={`bnav-item ${isActive('/admin') ? 'active' : ''}`}>
            <Shield size={22} />
            <span>{t('navbar.admin')}</span>
          </Link>
        )}

        {isRealUser ? (
          <button
            className="bnav-item avatar-trigger--mobile-bnav"
            popoverTarget="mobile-bnav-menu"
          >
            {userProfile?.photoURL ? (
              <img
                src={userProfile.photoURL}
                alt=""
                style={{ width: 24, height: 24, borderRadius: '50%', objectFit: 'cover' }}
              />
            ) : (
              <User size={22} />
            )}
            <span>{t('navbar.profile')}</span>
          </button>
        ) : (
          <Link to={ROUTES.login} className={`bnav-item ${isActive('/login') ? 'active' : ''}`}>
            <User size={22} />
            <span>{t('common.signIn')}</span>
          </Link>
        )}
      </nav>

      {/* ── Mobile notification bell ───────────────────────────────────────
          Rendered outside .navbar-container on purpose: on phones that
          container is display:none on every route except '/', so a bell inside
          it would be unreachable from Discover, the dashboard, event pages —
          which is everywhere a signed-in user actually is, since Landing
          redirects them off '/'. Floats rather than joining .bottom-nav
          because that already carries up to six items for an owner+admin.
          Desktop keeps its own bell inside the navbar. */}
      {isRealUser && (
        <div className="mobile-notif-overlay mobile-only">
          <NotifBell id="mobile" {...notifBellProps} />
        </div>
      )}

      {/* ── Avatar popover panels ──────────────────────────────────────────
          Each location (desktop topbar, mobile topbar, mobile bnav) has its
          own independent popover. popover="auto" ensures only one can be open
          at a time and handles light-dismiss + Escape natively — no JS needed.
          The duplicate "Log In" bug is eliminated structurally: there is no
          shared boolean state that can render two menus simultaneously.       */}
      <div id="desktop-avatar-menu" popover="auto" ref={desktopMenuRef} className="avatar-dropdown">
        <AvatarMenu {...avatarMenuProps} />
      </div>

      <div id="mobile-topbar-menu" popover="auto" ref={mobileTopbarRef} className="avatar-dropdown">
        <AvatarMenu {...avatarMenuProps} />
      </div>

      <div
        id="mobile-bnav-menu"
        popover="auto"
        ref={mobileBnavRef}
        className="avatar-dropdown avatar-dropdown--upward"
      >
        <AvatarMenu {...avatarMenuProps} />
      </div>
    </>
  );
};

export default Navbar;
